/**
 * Unified Badge Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef } from "react";
import { cn } from "../utils/cn";
import type { BadgeProps } from "./Badge.types";

const variantStyles: Record<string, string> = {
  // default: literal gray ramp WITH a dark: pair. Every SPA pins a STATIC gray ramp
  // under html.dark (unlayered, in its index.css) and the branded portal emits a
  // static ramp too, so a bare `bg-gray-100` stays #f1f5f9 in dark mode (a light pill
  // on a dark card). The inverting ramp in tokens-apps colors.css is mobile-only.
  // Literal stops compile in every consumer (semantic `bg-surface-subtle` does not).
  // Dark uses gray-600 (#475569), not gray-700: chips sit on gray-800 cards AND on
  // gray-700 rows / hovered table rows, where a gray-700 pill vanishes (1.0:1, measured
  // on the platform dashboard). gray-600 / gray-100 text = 6.9:1; light = 6.9:1.
  default: "bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-100",
  success: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  warning: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  // Informational — stays blue via semantic `info-*` tokens, independent of tenant brand.
  // See agents/plans/brand_blue_cleanup_2026-04-09.md for the info-vs-brand architecture.
  info: "bg-info-50 text-info-700 dark:bg-info-900/30 dark:text-info-300",
  // Brand — tracks the tenant brand color (Animas default = blue, tenant override possible).
  // text-brand-800 (not -700): brand-700 on brand-50 measures 4.29:1 (AA FAIL, Ladle a11y gate 2026-10-18).
  brand: "bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300",
};

const dotColors: Record<string, string> = {
  default: "bg-gray-400",
  success: "bg-green-500",
  warning: "bg-yellow-500",
  error: "bg-red-500",
  info: "bg-info-500",
  brand: "bg-brand-500",
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = "default", dot = false, className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-[var(--app-badge-radius,0.375rem)] px-[var(--app-badge-padding-x,0.5rem)] py-[var(--app-badge-padding-y,0.125rem)] text-[length:var(--app-badge-font-size,0.75rem)] font-medium",
          variantStyles[variant],
          className
        )}
        {...props}
      >
        {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotColors[variant])} aria-hidden="true" />}
        {children}
      </span>
    );
  }
);

Badge.displayName = "Badge";
